import React, { useState } from "react";
import BaseTextField from "../../../../components/ui/BaseTextField";
import { useDispatch, useSelector } from "react-redux";
import {
  updateWorkOrderForm,
  updateWorkOrderFormValidation,
} from "../../../../store/workOrder/workOrderSlice";
import { CurrencySymbol } from "../../../../utils/enum";
import BaseCheckbox from "../../../../components/ui/BaseCheckbox";
import { showToast } from "../../../../store/toast/toastSlice";

const EngineerHourlyPay = () => {
  const dispatch = useDispatch();
  const payment = useSelector((state) => state.workOrder.payment.pay);
  const workOrderFormValidation = useSelector((state) => state.workOrder.workOrderFormValidation);

  const [isMinimumHourError, setIsMinimumHourError] = useState(false);

  // An empty box and a zero/negative box are different mistakes, and the minimum
  // is quoted in the ENGINEER's currency — the customer's is a separate field.
  // CurrencySymbol maps to the code ("CDF"), not a glyph, so it needs a space.
  const additionalHourlyRateError = (value) => {
    if (value === "" || value === null || value === undefined) {
      return "Additional Hourly Rate is required";
    }

    const currency = CurrencySymbol[payment.engineerCurrency];
    return currency
      ? `Minimum Additional Hourly Rate is ${currency} 1`
      : "Minimum Additional Hourly Rate is 1";
  };

  const handleChange = (event) => {
    const { name, value } = event.target;
    const parsedValue = parseFloat(value);

    const payUpdate = { [name]: value };

    if (name === "engineerMinimumHours") {
      // Minimum hours are only meaningful next to a rate for the hours beyond
      // them — the validator rejects either one without the other — so tick the
      // Additional Hourly Rate checkbox as soon as minimum hours are entered,
      // and untick it (dropping the now-orphaned rate) when they're cleared.
      const hasMinimumHours = value !== "" && parsedValue > 0;
      payUpdate.enableEngineerAdditionalHourRate = hasMinimumHours;
      if (!hasMinimumHours) {
        // "" rather than null: the input is controlled off this value, and the
        // payload builders already treat an empty rate as null.
        payUpdate.engineerAdditionalHourlyRate = "";
        // The rate is no longer asked for, so its error has to go with it —
        // otherwise it sits in red under a disabled, empty field.
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerAdditionalHourlyRateError",
            data: "",
          }),
        );
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerAdditionalHourlyRate",
            data: false,
          }),
        );
      }
    }

    dispatch(
      updateWorkOrderForm({
        step: "payment",
        data: {
          pay: payUpdate,
        },
      }),
    );

    /*  if (name === "engineerMinimumHours") {
      if (!value || parseInt(value) === 0) {
        setIsMinimumHourError(true);
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerMinimumHours",
            data: true,
          })
        );
      } else {
        setIsMinimumHourError(false);
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerMinimumHours",
            data: false,
          })
        );
      }
    } */

    if (name === "engineerHourlyRate") {
      if (parsedValue <= 0) {
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerHourlyRateErrorMsg",
            data: `Engineer hourly rate cannot be less than ${
              CurrencySymbol[payment.engineerCurrency]
            }0`,
          }),
        );
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerHourlyRate",
            data: true,
          }),
        );
      } else {
        dispatch(
          updateWorkOrderFormValidation({
            fieldName: "engineerHourlyRate",
            data: false,
          }),
        );
      }
    }

    if (
      name === "engineerAdditionalHourlyRate" &&
      payment.enableEngineerAdditionalHourRate &&
      value <= 0
    ) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "engineerAdditionalHourlyRateError",
          data: additionalHourlyRateError(value),
        }),
      );
    }

    if (
      name === "engineerAdditionalHourlyRate" &&
      payment.enableEngineerAdditionalHourRate &&
      value > 0
    ) {
      dispatch(
        updateWorkOrderFormValidation({
          fieldName: "engineerAdditionalHourlyRateError",
          data: "",
        }),
      );
    }
  };

  return (
    <div className="grid grid-cols-2 gap-y-4 mt-3">
      <div className="lg:mr-10 lg:col-span-1 col-span-2">
        <BaseTextField
          onInput={(e) => (e.target.value = e.target.value.replace(/-/g, ""))}
          error={workOrderFormValidation.engineerHourlyRate}
          errorMsg={workOrderFormValidation.engineerHourlyRateErrorMsg}
          fullWidth
          fullHeight={false}
          id="engineerHourlyRate"
          name="engineerHourlyRate"
          label="Hourly Rate"
          requirement="required"
          labelShrink={false}
          value={payment.engineerHourlyRate}
          onChange={handleChange}
          isDefaultField={false}
          type="float"
          // bottomRightMsg="The hourly rate should be no less than $20 per hour"
        />
      </div>
      <div className="lg:mr-10 lg:col-span-1 col-span-2">
        <BaseTextField
          onInput={(e) => (e.target.value = e.target.value.replace(/-/g, ""))}
          error={workOrderFormValidation.engineerAdditionalHourlyRateError}
          errorMsg={workOrderFormValidation.engineerAdditionalHourlyRateError}
          fullWidth
          fullHeight={false}
          disabled={!payment.enableEngineerAdditionalHourRate}
          id="engineerAdditionalHourlyRate"
          name="engineerAdditionalHourlyRate"
          label={
            <div className="flex">
              <p className="mr-2 font-normal">
                Additional Hourly Rate
                {/* Required once opted in — and the validator also demands it
                    whenever engineer minimum hours are set. */}
                {payment.enableEngineerAdditionalHourRate && (
                  <span className="text-red-500 ml-0.5" aria-hidden="true">
                    *
                  </span>
                )}
              </p>
              <BaseCheckbox
                id="enableEngineerAdditionalHourRate"
                name="enableEngineerAdditionalHourRate"
                checked={payment.enableEngineerAdditionalHourRate || false}
                onChange={() => {
                  // Minimum hours can't stand without a rate for the hours past
                  // them, so the box stays ticked until they're cleared.
                  if (
                    payment.enableEngineerAdditionalHourRate &&
                    parseFloat(payment.engineerMinimumHours) > 0
                  ) {
                    dispatch(
                      showToast({
                        message: "Clear Minimum Hours first",
                        type: "error",
                      }),
                    );
                    return;
                  }

                  if (payment.enableEngineerAdditionalHourRate) {
                    dispatch(
                      updateWorkOrderFormValidation({
                        fieldName: "engineerAdditionalHourlyRateError",
                        data: "",
                      }),
                    );
                  }
                  if (
                    !payment.enableEngineerAdditionalHourRate &&
                    payment.engineerAdditionalHourlyRate <= 0
                  ) {
                    dispatch(
                      updateWorkOrderFormValidation({
                        fieldName: "engineerAdditionalHourlyRateError",
                        data: additionalHourlyRateError(payment.engineerAdditionalHourlyRate),
                      }),
                    );
                  }
                  dispatch(
                    updateWorkOrderForm({
                      step: "payment",
                      data: {
                        pay: {
                          ...payment,
                          enableEngineerAdditionalHourRate:
                            !payment.enableEngineerAdditionalHourRate,
                        },
                      },
                    }),
                  );
                }}
              />
            </div>
          }
          optional={false}
          labelShrink={false}
          value={payment.engineerAdditionalHourlyRate}
          onChange={handleChange}
          isDefaultField={false}
          type="float"
        />
      </div>
      <div className="lg:col-span-1 col-span-2 lg:mr-10 ">
        <BaseTextField
          onInput={(e) => (e.target.value = e.target.value.replace(/-/g, ""))}
          error={workOrderFormValidation.engineerMinimumHours || isMinimumHourError}
          errorMsg="Minimum 1 hour is required"
          fullWidth
          fullHeight={false}
          id="engineerMinimumHours"
          name="engineerMinimumHours"
          label="Minimum Hours"
          optional={false}
          labelShrink={false}
          required={true}
          value={payment.engineerMinimumHours}
          onChange={handleChange}
          isDefaultField={false}
          type="float"
          bottomRightMsg="Minimum Payable Hours"
        />
      </div>
    </div>
  );
};

export default EngineerHourlyPay;
